import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { Visibility as ViewIcon, Refresh as RefreshIcon, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { runsApi } from '../services/api';
import { workloadsApi } from '../services/workloadsService';
import { roomsApi } from '../services/roomsService';
import { Run, PaginatedResponse, WorkloadDefinition, SchulungsraumDefinition } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';
import { TableSkeleton } from '../components/SkeletonLoaders';
import { RunVerdictChip } from '../components/RunVerificationPanel';
import { ShutdownEvidenceChip } from '../components/ShutdownEvidenceChip';
import { RunClassificationChip } from '../components/RunClassificationChip';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import RunCreateDialog from '../components/RunCreateDialog';
import { useAuth } from '../contexts/AuthContext';
import { useApiQuery } from '../hooks/useApiQuery';

// NC-1077: the status dropdown mixes two "view" meta-options (active/all) with
// single terminal statuses. VIEW_VALUES are sent to the API as `view=`, the
// rest as `status=`. Default is 'active' so finished/failed runs are hidden by
// default but never deleted — the operator switches to 'all' to see history.
const VIEW_VALUES = new Set(['active', 'all']);

export default function RunsPage() {
  const [workloadFilter, setWorkloadFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  // NC-1288: '' = alle, sonst 'produktiv' | 'test'.
  const [classificationFilter, setClassificationFilter] = useState<string>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [workloads, setWorkloads] = useState<WorkloadDefinition[]>([]);
  const [rooms, setRooms] = useState<SchulungsraumDefinition[]>([]);
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  useEffect(() => {
    workloadsApi
      .list()
      .then((data) => setWorkloads(data))
      .catch(() => setWorkloads([]));
    // NC-798: load all rooms (incl. inactive) so historical runs still resolve
    // a room name after the room is deactivated.
    roomsApi
      .list()
      .then((data) => setRooms(data))
      .catch(() => setRooms([]));
  }, []);

  const workloadById = useMemo(() => {
    const map = new Map<string, WorkloadDefinition>();
    for (const w of workloads) map.set(w.id, w);
    return map;
  }, [workloads]);

  const roomById = useMemo(() => {
    const map = new Map<string, SchulungsraumDefinition>();
    for (const r of rooms) map.set(r.id, r);
    return map;
  }, [rooms]);

  const isView = VIEW_VALUES.has(statusFilter);
  const { data, loading, error, refetch } = useApiQuery<PaginatedResponse<Run>>(
    () => runsApi.list({
      workload: workloadFilter || undefined,
      view: isView ? (statusFilter as 'active' | 'all') : undefined,
      status: !isView && statusFilter ? statusFilter : undefined,
      classification:
        (classificationFilter as 'produktiv' | 'test') || undefined,
      pageSize: 100,
    }),
    [workloadFilter, statusFilter, classificationFilter]
  );

  const runs = data?.items || [];

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'planned':
        return 'info';
      case 'provisioning':
        return 'info';
      case 'active':
        return 'success';
      case 'stopped':
        return 'warning';
      case 'expired':
        return 'default';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getWorkloadDisplay = (run: Run): { label: string; color: 'primary' | 'secondary' | 'default' } => {
    const id = run.workloadId ?? run.workload;
    const def = id ? workloadById.get(id) : undefined;
    const label = def?.name ?? (id ? id.toUpperCase() : '—');
    const category = def?.category ?? run.workload;
    const color = category === 'cam' ? 'primary' : category === 'bim' ? 'secondary' : 'default';
    return { label, color };
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'planned':
        return 'Geplant';
      case 'provisioning':
        return 'Wird erstellt';
      case 'active':
        return 'Aktiv';
      case 'stopped':
        return 'Gestoppt';
      case 'expired':
        return 'Abgelaufen';
      case 'error':
        return 'Fehler';
      default:
        return 'Unbekannt';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">
          Schulungsläufe
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refetch}
            disabled={loading}
          >
            Aktualisieren
          </Button>
          {hasRole('operator') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Neue Schulung
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <ErrorMessage
          message={error}
          title="Fehler beim Laden der Schulungsläufe"
          onRetry={refetch}
        />
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Workload"
                value={workloadFilter}
                onChange={(e) => setWorkloadFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="">Alle</MenuItem>
                {workloads.length > 0
                  ? workloads.map((w) => (
                      <MenuItem key={w.id} value={w.id}>
                        {w.name}
                      </MenuItem>
                    ))
                  : [
                      <MenuItem key="cam" value="cam">
                        CAM
                      </MenuItem>,
                      <MenuItem key="bim" value="bim">
                        BIM
                      </MenuItem>,
                    ]}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="small"
              >
                {/* NC-1077: 'Aktive' (default) hides terminal runs; 'Alle' shows
                    the full history; the terminal statuses stay selectable so an
                    operator can isolate expired/failed runs. */}
                <MenuItem value="active">Aktive</MenuItem>
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="stopped">Gestoppt</MenuItem>
                <MenuItem value="expired">Abgelaufen</MenuItem>
                <MenuItem value="error">Fehler</MenuItem>
              </TextField>
            </Grid>
            {/* NC-1288: reporting classification. Composes with the workload and
                status filters; runs-list applies it in memory so legacy E2E runs
                without a persisted marker are still matched. */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Art"
                value={classificationFilter}
                onChange={(e) => setClassificationFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="">Alle</MenuItem>
                <MenuItem value="produktiv">Produktiv</MenuItem>
                <MenuItem value="test">Test</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        {loading && runs.length === 0 ? (
          <TableSkeleton
            rows={5}
            columns={9}
            headers={['Run ID', 'Workload', 'Raum', 'Kurs', 'Teilnehmer', 'Beginnt', 'Läuft ab', 'Status', 'Aktionen']}
          />
        ) : runs.length === 0 ? (
          statusFilter === 'active' && !workloadFilter ? (
            <EmptyState
              variant="no-results"
              title="Keine aktiven Schulungsläufe"
              description={
                'Abgeschlossene und fehlgeschlagene Läufe werden nicht gelöscht — ' +
                'wählen Sie „Alle", um die vollständige Historie anzuzeigen.'
              }
              action={{
                label: 'Alle anzeigen',
                onClick: () => setStatusFilter('all'),
                icon: <RefreshIcon />,
              }}
            />
          ) : (
            <EmptyState
              variant="no-results"
              title="Keine Schulungsläufe gefunden"
              description="Mit den aktuellen Filtern wurden keine Läufe gefunden. Versuchen Sie, die Filter zurückzusetzen."
              action={{
                label: 'Filter zurücksetzen',
                onClick: () => {
                  setWorkloadFilter('');
                  setStatusFilter('active');
                },
                icon: <RefreshIcon />,
              }}
            />
          )
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Run ID</TableCell>
                  <TableCell>Workload</TableCell>
                  <TableCell>Raum</TableCell>
                  <TableCell>Kurs</TableCell>
                  <TableCell align="center">Teilnehmer</TableCell>
                  <TableCell>Beginnt</TableCell>
                  <TableCell>Läuft ab</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {run.runId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const wl = getWorkloadDisplay(run);
                        return <Chip label={wl.label} color={wl.color} size="small" />;
                      })()}
                    </TableCell>
                    <TableCell>
                      {(run.roomId ? roomById.get(run.roomId)?.name : undefined) ?? run.roomId ?? '—'}
                    </TableCell>
                    <TableCell>{run.course}</TableCell>
                    <TableCell align="center">{run.accounts?.length || 0}</TableCell>
                    <TableCell>
                      {formatDateSafe(run.scheduledStart ?? run.createdAtUtc)}
                    </TableCell>
                    <TableCell>
                      {formatDateSafe(run.expireOn)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        <Chip
                          label={getStatusLabel(run.status)}
                          color={getStatusColor(run.status)}
                          size="small"
                        />
                        {/* NC-1235 (V2): last acceptance verdict alongside the run
                            status. Absent until a verify has run + V1 (#1234)
                            persists the report, so it renders conditionally. */}
                        {run.lastVerification && (
                          <RunVerdictChip verdict={run.lastVerification.verdict} />
                        )}
                        {/* NC-1289: shutdown/cleanup evidence straight from the
                            run doc — the list view cannot afford a live
                            power-state call per row. */}
                        <RunClassificationChip classification={run.classification} />
                        <ShutdownEvidenceChip run={run} />
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Details anzeigen">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/runs/${run.runId}`)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <RunCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={refetch}
        // NC-1567: the evening-shutdown opt-out is an admin-only cost decision
        // (runs-create rejects it server-side for operators as well).
        canKeepRunning={hasRole('admin')}
      />
    </Box>
  );
}
